import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import {
  Creator,
  FormProviders,
  FormRequest,
  FormRequestTemplate,
  FormStatus,
  MESSAGE_ATTACHMENT_REFERENCE_TYPES,
  Person,
} from '@typings/api-models';
import ListBullets from '@components/Icons/ListBullets';
import CheckCircle from '@components/Icons/CheckCircle';
import Integration from '@components/Icons/Integration';
import CaretRight from '@components/Icons/CaretRight';
import Buttons from '@components/Buttons';
import { useUser } from '@hooks';
import * as tsClient from '@tsClient';
import useNoodleApi from '@hooks/useNoodleApi';
import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import DashboardContext from '@layouts/DashboardLayout/DashboardContext';
import SkipTaskConfirmationModal from '@modals/SkipTaskConfirmationModal';
import { useOpenFormRequestPane } from '@panes/hooks';
import UserProfileContext from '@layouts/UserProfileLayout/UserProfileContext';
import * as format from '@format';
import s from './FormRequestCard.module.scss';
import Tooltip from '../Tooltip';

type Props = {
  creator: Pick<Creator, 'slug'>;
  request: Pick<FormRequest, 'id' | 'url' | 'status'> & {
    template: Pick<FormRequestTemplate, 'id' | 'name' | 'provider'>;
    person: Pick<Person, 'id'>;
  };
  canClick: boolean;
  canSkip: boolean;
  setIsFulfilled?: (b: boolean) => void;
  setIsSkipped?: (b: boolean) => void;
  onChange?: () => void;
  completedAt?: string | null;
  integrationSlugs?: string[];
};

const FormRequestCard: React.FC<Props> = ({
  setIsFulfilled,
  setIsSkipped,
  onChange,
  canClick,
  canSkip,
  creator,
  request,
  completedAt,
  integrationSlugs,
}) => {
  const router = useRouter();
  const [user] = useUser();
  const openFormRequestPane = useOpenFormRequestPane();

  const [skipTaskModalOpen, setSkipTaskModalOpen] = useState(false);
  const { isInDashboard } = useContext(DashboardContext);
  const { isInUserProfile } = useContext(UserProfileContext);

  const { getData: updateFormRequest } = useNoodleApi(tsClient.formRequests.updateFormRequest, {
    toastOnError: true,
  });

  const isCompleted = request.status === FormStatus.COMPLETED;
  const isSkipped = request.status === FormStatus.SKIPPED;

  useEffect(() => {
    setIsFulfilled?.(isCompleted);
  }, [isCompleted, setIsFulfilled]);

  useEffect(() => {
    setIsSkipped?.(isSkipped);
  }, [isSkipped, setIsSkipped]);

  const handleClick = (): void => {
    if (isInDashboard) {
      if (typeof router.query.userWorkflowId === 'string') {
        window.open(getUrl(IDENTIFIERS.DASHBOARD_FORM_REQUEST_DETAILS, { formRequestId: request.id, userWorkflowId: router.query.userWorkflowId }), "_blank");
      } else {
        openFormRequestPane({ formRequestId: request.id });
      }
    } else if (user && request.person.id === user.id && !isCompleted && request.template.provider === FormProviders.Typeform) {
      window.open(request.url, '_blank');
    } else if (isInUserProfile) {
      openFormRequestPane({ formRequestId: request.id });
    } else {
      router.push(getUrl(IDENTIFIERS.FORM_REQUEST_DETAILS, { creatorSlug: creator.slug, formRequestId: request.id }));
    }
  };

  const handleSkipTask = async (): Promise<void> => {
    await updateFormRequest({
      formRequestId: request.id,
      status: FormStatus.SKIPPED,
    });
    setSkipTaskModalOpen(false);
    if (onChange) onChange();
  };

  return (
    <>
      {skipTaskModalOpen && (
        <SkipTaskConfirmationModal
          onClose={() => setSkipTaskModalOpen(false)}
          onConfirm={handleSkipTask}
          taskType={MESSAGE_ATTACHMENT_REFERENCE_TYPES.FORM_REQUEST}
        />
      )}
      <Buttons
        className={s.container}
        isFullWidth
        isWrapper
        key={request.id}
        onClick={handleClick}
        disabled={!canClick || (isSkipped && !canSkip)}
        style={isSkipped ? { backgroundColor: 'var(--color-gray-25)', borderRadius: 8 } : {}}
      >
        {isCompleted
          ? <CheckCircle size={16} weight="fill" color="var(--color-success)" />
          : <figure>
            <ListBullets size={20} color="var(--color-gray-100)" />
          </figure>}
        <div>
          <p
            className={isCompleted ? "caption-bold" : "body-sm-bold"}
            style={isCompleted ? { color: 'var(--color-gray-75)' } : { lineHeight: 1.4 }}
          >
            {request.template.name}
          </p>
          {((completedAt && isCompleted) || (isInDashboard && (integrationSlugs?.length || 0) > 0)) && (
            <div className={s.type}>
              {(completedAt && isCompleted) && <p className="caption">Completed on {format.datetimeLocalTZ.withoutTimezone({ datetime: completedAt })}</p>}
              {isInDashboard && (integrationSlugs?.length || 0) > 0 && (
                <Tooltip text={`Data in this form is going to eFile with the ${format.csl(integrationSlugs?.map(slug => slug.toUpperCase()) || [])}`}>
                  <Integration weight="fill" color="var(--color-noodle)" size={12} />
                </Tooltip>
              )}
            </div>
          )}
          {isSkipped && <p className="caption">This task is no longer required.</p>}
        </div>
        {canClick && (!isSkipped || canSkip) && (
          <>
            {isCompleted
              ? <CaretRight size={12} color="var(--color-gray-100)" />
              : <div
                className={s.open}
                {...(!isCompleted
                  && !isInDashboard && {
                  style: {
                    backgroundColor: 'var(--color-primary)',
                    border: '1px solid var(--color-primary)',
                    color: 'var(--color-gray-0)',
                  },
                })}
              >
                View
              </div>}
          </>
        )}
      </Buttons>
      {canSkip && !isSkipped && (
        <Buttons
          onClick={event => {
            event.stopPropagation();
            setSkipTaskModalOpen(true);
          }}
          className={s.skip}
        >
          Skip
        </Buttons>
      )}
    </>
  );
};

export default FormRequestCard;
